import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser, useAuth } from "../hooks/useAuthCompat";
import { toast } from "sonner";
import { useApi, publicApi } from "../hooks/useApi";
import { useAuthContext } from "../context/AuthContext";
import { CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";

export default function TMSActivate() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const ticket = params.get("ticket");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const api = useApi();
  const { isSignedIn, isLoaded } = useUser();
  const { signOut } = useAuth();
  const { refresh } = useAuthContext();
  const attempted = useRef(false);
  const ticketAttempted = useRef(false);
  const [slowWait, setSlowWait] = useState(false);
  const [ticketFailed, setTicketFailed] = useState(false);
  const [consumingTicket, setConsumingTicket] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setSlowWait(true), 60000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  // Phase 9.4: redeem our own first-party, passwordless activation token
  // from the activation email — signs the purchaser in automatically, no
  // password, no pre-existing account required.
  useEffect(() => {
    if (!ticket || isSignedIn || ticketAttempted.current) return;
    ticketAttempted.current = true;
    setConsumingTicket(true);
    (async () => {
      try {
        await publicApi.post("/auth/activate", { token: ticket });
        await refresh();
      } catch {
        setTicketFailed(true);
      } finally {
        setConsumingTicket(false);
      }
    })();
  }, [ticket, isSignedIn, refresh]);

  const { data: claim, isError: claimNotFound } = useQuery({
    queryKey: ["tms-claim", sessionId],
    queryFn: async () => (await api.get("/school/enroll/claim", { params: { session_id: sessionId } })).data,
    enabled: !!sessionId,
    retry: false,
    refetchInterval: (query) => (query.state.status === "error" || ["claimed", "paid", "refunded"].includes(query.state.data?.status) ? false : 2500),
  });

  useEffect(() => {
    if (typeof claim?.resend_available_in_seconds === "number") setResendCooldown(claim.resend_available_in_seconds);
  }, [claim?.resend_available_in_seconds]);

  const redeem = useMutation({
    mutationFn: async () => api.post("/school/enroll/claim/redeem", { session_id: sessionId }),
    onSuccess: () => navigate("/themuscleschool/dashboard", { replace: true }),
  });

  const resend = useMutation({
    mutationFn: async () => publicApi.post("/school/enroll/claim/resend", { session_id: sessionId }),
    onSuccess: (res) => {
      toast.success("A new activation link is on its way to your email.");
      setResendCooldown(res.data?.resend_available_in_seconds || 60);
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message || "Could not send a new link.";
      toast.error(message);
      if (detail?.retry_after_seconds) setResendCooldown(detail.retry_after_seconds);
      if (e?.response?.status === 410) queryClient.invalidateQueries({ queryKey: ["tms-claim", sessionId] });
    },
  });

  useEffect(() => {
    const ready = claim?.status === "paid" || claim?.status === "claimed";
    if (ready && isLoaded && isSignedIn && !attempted.current && !redeem.isPending) {
      attempted.current = true;
      redeem.mutate();
    }
  }, [claim, isLoaded, isSignedIn, redeem]);

  if (!sessionId) {
    return (
      <div className="theme-school min-h-screen flex items-center justify-center text-center px-6" data-testid="tms-activate-missing-session">
        <p className="text-textSecondary">Missing checkout session. <Link to="/themuscleschool" className="underline text-primary">Return to The Muscle School</Link></p>
      </div>
    );
  }

  if (claimNotFound) {
    return (
      <div className="theme-school min-h-screen flex items-center justify-center text-center px-6" data-testid="tms-activate-not-found">
        <div>
          <p className="text-textSecondary mb-4">We couldn't find a Muscle School checkout for this session.</p>
          <p className="text-textSecondary text-sm mb-6">If you just paid, check your email for an activation link, or <a href="mailto:support@endurebeyondpod.com" className="underline text-primary">contact support</a>.</p>
          <Link to="/themuscleschool" className="underline text-primary">Return to The Muscle School</Link>
        </div>
      </div>
    );
  }

  if (claim?.status === "refunded") {
    return (
      <div className="theme-school min-h-screen flex items-center justify-center text-center px-6" data-testid="tms-activate-refunded">
        <div>
          <h1 className="font-display text-xl mb-2">This Purchase Was Refunded</h1>
          <p className="text-textSecondary text-sm mb-6">
            This enrollment can no longer be activated. If you believe this is a mistake, <a href="mailto:support@endurebeyondpod.com" className="underline text-primary">contact support</a>.
          </p>
          <Link to="/themuscleschool" className="underline text-primary">Return to The Muscle School</Link>
        </div>
      </div>
    );
  }

  const paid = claim?.status === "paid" || claim?.status === "claimed";
  const mismatchError = redeem.isError ? (redeem.error?.response?.data?.detail || "Could not activate your enrollment.") : null;

  return (
    <div className="theme-school min-h-screen flex items-center justify-center px-6 py-16" data-testid="tms-activate-page">
      <div className="max-w-md w-full text-center">
        <img src="/tms/seal.png" alt="" className="w-14 h-14 rounded-full object-cover border border-primary/20 mx-auto mb-6" />

        {!paid && (
          <div data-testid="tms-activate-confirming" className="flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <p className="text-textSecondary text-sm">Confirming your payment with Stripe…</p>
            {slowWait && (
              <p className="text-textSecondary text-xs mt-4" data-testid="tms-activate-slow-wait">
                This is taking longer than expected. If you completed payment, check your email for an activation
                link, or <a href="mailto:support@endurebeyondpod.com" className="underline text-primary">contact support</a>.
              </p>
            )}
          </div>
        )}

        {paid && !isSignedIn && consumingTicket && (
          <div data-testid="tms-activate-ticket-consuming" className="flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <p className="text-textSecondary text-sm">Activating your account…</p>
          </div>
        )}

        {paid && !isSignedIn && !consumingTicket && ticketFailed && claim?.status !== "claimed" && (
          <div data-testid="tms-activate-ticket-failed">
            <h1 className="font-display text-xl mb-2">This Link Has Expired</h1>
            <p className="text-textSecondary text-sm mb-6">
              Your activation link for <span className="text-foreground font-medium">{claim.email}</span> is no longer valid.
              Request a fresh one, or create your password below.
            </p>
            <button
              data-testid="tms-activate-resend-button"
              onClick={() => resend.mutate()}
              disabled={resend.isPending || resendCooldown > 0}
              className="bg-primary text-primary-foreground px-5 py-2.5 text-sm uppercase tracking-wide hover:opacity-90 transition-opacity disabled:opacity-50 mb-6"
            >
              {resend.isPending ? "Sending…" : resendCooldown > 0 ? `Try Again In ${resendCooldown}s` : "Send Me a New Link"}
            </button>
            <Link to={`/sign-up?email=${encodeURIComponent(claim.email)}`} data-testid="tms-activate-sign-up-link"
              className="block bg-[#fffdf8] border border-border py-3 text-sm text-foreground hover:border-primary transition-colors">
              Set a password for {claim.email}
            </Link>
          </div>
        )}

        {paid && !isSignedIn && !consumingTicket && ticketFailed && claim?.status === "claimed" && (
          <div data-testid="tms-activate-already-claimed">
            <CheckCircle2 className="w-8 h-8 text-primary mx-auto mb-3" />
            <h1 className="font-display text-xl mb-2">Already Activated</h1>
            <p className="text-textSecondary text-sm mb-6">
              This enrollment is already active on an account. Sign in with <span className="text-foreground font-medium">{claim.email}</span> below to open your curriculum.
            </p>
            <Link to={`/sign-in?email=${encodeURIComponent(claim.email)}`} data-testid="tms-activate-sign-in-link"
              className="block bg-[#fffdf8] border border-border py-3 text-sm text-foreground hover:border-primary transition-colors">
              Sign In as {claim.email}
            </Link>
          </div>
        )}

        {paid && !isSignedIn && !consumingTicket && !ticketFailed && !ticket && (
          <div data-testid="tms-activate-signin-prompt">
            <CheckCircle2 className="w-8 h-8 text-primary mx-auto mb-3" />
            <h1 className="font-display text-xl mb-2">Payment Confirmed</h1>
            <p className="text-textSecondary text-sm mb-6">
              Check your email for an activation link, or sign in below with <span className="text-foreground font-medium">{claim.email}</span>.
            </p>
            <Link to={`/sign-in?email=${encodeURIComponent(claim.email)}`} data-testid="tms-activate-sign-in-link"
              className="block bg-[#fffdf8] border border-border py-3 text-sm text-foreground hover:border-primary transition-colors">
              Sign In as {claim.email}
            </Link>
          </div>
        )}

        {paid && isSignedIn && !mismatchError && (
          <div data-testid="tms-activate-redeeming" className="flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <p className="text-textSecondary text-sm">Activating your enrollment…</p>
          </div>
        )}

        {mismatchError && (
          <div data-testid="tms-activate-mismatch-error">
            <h1 className="font-display text-xl mb-3 text-primary">Email Does Not Match</h1>
            <p className="text-textSecondary text-sm mb-6">{mismatchError}</p>
            <button
              data-testid="tms-activate-signout-button"
              onClick={() => signOut()}
              className="bg-secondary text-secondary-foreground px-5 py-2.5 text-sm uppercase tracking-wide border border-border hover:border-primary transition-colors"
            >
              Sign Out &amp; Try Another Account
            </button>
          </div>
        )}

        <p className="text-xs text-textSecondary/70 flex items-center justify-center gap-1.5 mt-10">
          <ShieldCheck className="w-3.5 h-3.5" /> Verified via Stripe · Secured by Endure Beyond
        </p>
      </div>
    </div>
  );
}
